import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Trophy, 
  Target, 
  Users, 
  Star, 
  Award, 
  TrendingUp, 
  Calendar,
  Clock,
  Zap,
  Crown,
  Medal,
  Gift,
  ChevronRight,
  Plus,
  Filter,
  Search
} from 'lucide-react';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  points: number;
  category: 'workout' | 'nutrition' | 'streak' | 'social' | 'progress' | 'challenge';
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  unlockedAt?: Date;
  progress?: number;
  maxProgress?: number;
}

interface Challenge {
  id: string;
  name: string;
  description: string;
  type: 'individual' | 'group' | 'community';
  category: 'workout' | 'nutrition' | 'streak' | 'social';
  startDate: Date;
  endDate: Date;
  participants: string[];
  rewards: Reward[];
  leaderboard: LeaderboardEntry[];
  status: 'upcoming' | 'active' | 'completed';
}

interface LeaderboardEntry {
  userId: string;
  username: string;
  score: number;
  rank: number;
  avatar?: string;
  badges: string[];
}

interface Reward {
  id: string;
  type: 'points' | 'badge' | 'discount' | 'premium_feature' | 'merchandise';
  value: any;
  description: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

interface Streak {
  type: 'workout' | 'nutrition' | 'check_in' | 'challenge';
  current: number;
  longest: number;
  lastActivity: Date;
  milestones: number[];
}

const GamificationPanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState('achievements');
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [streaks, setStreaks] = useState<Streak[]>([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [engagementLevel, setEngagementLevel] = useState('Beginner');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadGamificationData();
  }, []);

  const loadGamificationData = async () => {
    try {
      setLoading(true);
      // Load achievements, challenges, leaderboard, and streaks
      const [achievementsData, challengesData, leaderboardData, streaksData] = await Promise.all([
        loadAchievements(),
        loadChallenges(),
        loadLeaderboard(),
        loadStreaks()
      ]);

      setAchievements(achievementsData);
      setChallenges(challengesData);
      setLeaderboard(leaderboardData);
      setStreaks(streaksData);
      setTotalPoints(achievementsData.reduce((sum, achievement) => sum + achievement.points, 0));
      setEngagementLevel(calculateEngagementLevel(totalPoints));
    } catch (error) {
      console.error('Error loading gamification data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAchievements = async (): Promise<Achievement[]> => {
    // Mock data - replace with actual API call
    return [
      {
        id: 'first_week',
        name: 'First Week Warrior',
        description: 'Complete 7 workouts in a week',
        icon: '🏋️‍♀️',
        points: 100,
        category: 'workout',
        rarity: 'common',
        unlockedAt: new Date(),
        progress: 7,
        maxProgress: 7
      },
      {
        id: 'nutrition_tracker',
        name: 'Nutrition Tracker',
        description: 'Log 21 meals in a week',
        icon: '🥗',
        points: 150,
        category: 'nutrition',
        rarity: 'common',
        unlockedAt: new Date(),
        progress: 21,
        maxProgress: 21
      },
      {
        id: 'week_streak',
        name: 'Week Streak',
        description: '7-day workout streak',
        icon: '🔥',
        points: 200,
        category: 'streak',
        rarity: 'rare',
        unlockedAt: new Date(),
        progress: 7,
        maxProgress: 7
      }
    ];
  };

  const loadChallenges = async (): Promise<Challenge[]> => {
    // Mock data - replace with actual API call
    return [
      {
        id: 'challenge_1',
        name: '30-Day Fitness Challenge',
        description: 'Complete 30 workouts in 30 days',
        type: 'community',
        category: 'workout',
        startDate: new Date(),
        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        participants: ['user1', 'user2', 'user3'],
        rewards: [
          {
            id: 'reward_1',
            type: 'points',
            value: 500,
            description: '500 bonus points',
            rarity: 'rare'
          }
        ],
        leaderboard: [],
        status: 'active'
      }
    ];
  };

  const loadLeaderboard = async (): Promise<LeaderboardEntry[]> => {
    // Mock data - replace with actual API call
    return [
      {
        userId: 'user1',
        username: 'FitnessFanatic',
        score: 2500,
        rank: 1,
        badges: ['🏆', '🔥', '💪']
      },
      {
        userId: 'user2',
        username: 'WorkoutWarrior',
        score: 2200,
        rank: 2,
        badges: ['🔥', '💪']
      },
      {
        userId: 'user3',
        username: 'HealthHero',
        score: 2000,
        rank: 3,
        badges: ['💪']
      }
    ];
  };

  const loadStreaks = async (): Promise<Streak[]> => {
    // Mock data - replace with actual API call
    return [
      {
        type: 'workout',
        current: 7,
        longest: 12,
        lastActivity: new Date(),
        milestones: [3, 7]
      },
      {
        type: 'nutrition',
        current: 5,
        longest: 8,
        lastActivity: new Date(),
        milestones: [3, 5]
      }
    ];
  };

  const calculateEngagementLevel = (points: number): string => {
    if (points < 100) return 'Beginner';
    if (points < 500) return 'Active';
    if (points < 1000) return 'Dedicated';
    if (points < 2500) return 'Expert';
    return 'Master';
  };

  const getRarityColor = (rarity: string): string => {
    switch (rarity) {
      case 'common': return 'text-gray-800';
      case 'rare': return 'text-blue-800';
      case 'epic': return 'bg-purple-100 text-purple-800';
      case 'legendary': return 'bg-yellow-100 text-yellow-800';
      default: return 'text-gray-800';
    }
  };

  const getRarityStyle = (rarity: string) => {
    switch (rarity) {
      case 'common': return { background: 'var(--color-background-secondary)', color: 'var(--color-text)' };
      case 'rare': return { background: 'var(--gradient-cta-primary)', color: 'var(--color-white)', boxShadow: 'var(--shadow-lg)' };
      case 'epic': return { background: 'var(--color-secondary)', color: 'var(--color-white)' };
      case 'legendary': return { background: 'var(--color-accent)', color: 'var(--color-white)' };
      default: return { background: 'var(--color-background-secondary)', color: 'var(--color-text)' };
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'workout': return <Zap className="w-4 h-4" />;
      case 'nutrition': return <Target className="w-4 h-4" />;
      case 'streak': return <TrendingUp className="w-4 h-4" />;
      case 'social': return <Users className="w-4 h-4" />;
      case 'progress': return <TrendingUp className="w-4 h-4" />;
      case 'challenge': return <Trophy className="w-4 h-4" />;
      default: return <Star className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-primary)' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-end pr-4">
        <div className="text-right">
          <div className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>{totalPoints}</div>
          <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Total Points</div>
        </div>
      </div>

      {/* Engagement Level */}
      <div className="sculptd-card-elevated sculptd-sheen" style={{
        boxShadow: 'var(--shadow-card-separation)'
      }}>
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-full" style={{ background: 'var(--color-primary-light)' }}>
                <Crown className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--color-text)' }}>Engagement Level</h3>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{engagementLevel}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>{engagementLevel}</div>
              <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>Current Level</div>
            </div>
          </div>
        </div>
      </div>

      {/* Streaks */}
      <div className="sculptd-card-elevated sculptd-sheen" style={{
        boxShadow: 'var(--shadow-card-separation)'
      }}>
        <div className="sculptd-container-padding mb-6">
          <h3 className="flex items-center space-x-3 text-xl font-semibold" style={{ 
            color: 'var(--color-text)',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <TrendingUp className="w-6 h-6" style={{ color: 'var(--color-primary)' }} />
            <span>Current Streaks</span>
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {streaks.map((streak) => (
              <div key={streak.type} className="sculptd-card-elevated sculptd-sheen sculptd-card-padding rounded-lg" style={{
                boxShadow: 'var(--shadow-card-separation)'
              }}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold capitalize">{streak.type} Streak</h4>
                  <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                    background: 'var(--color-secondary)', 
                    color: 'var(--color-white)' 
                  }}>{streak.current} days</span>
                </div>
                <div className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                  Longest: {streak.longest} days
                </div>
                <div className="mt-2">
                  <div className="sculptd-progress-bar h-2 w-full">
                    <div 
                      className="sculptd-progress-fill h-full"
                      style={{ width: `${(streak.current / streak.longest) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="achievements">Achievements</TabsTrigger>
          <TabsTrigger value="challenges">Challenges</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
          <TabsTrigger value="rewards">Rewards</TabsTrigger>
        </TabsList>

        {/* Achievements Tab */}
        <TabsContent value="achievements" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {achievements.map((achievement) => (
              <Card key={achievement.id} className={`sculptd-card hover:shadow-lg transition-all duration-500 relative overflow-hidden ${achievement.unlockedAt ? 'ring-2 ring-gradient-celebration' : ''}`} style={{
                background: achievement.unlockedAt 
                  ? 'linear-gradient(135deg, rgba(156, 106, 222, 0.15) 0%, rgba(244, 182, 194, 0.1) 50%, rgba(58, 175, 169, 0.05) 100%)'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                boxShadow: achievement.unlockedAt 
                  ? '0 16px 64px rgba(156, 106, 222, 0.2), 0 8px 32px rgba(244, 182, 194, 0.1)'
                  : '0 16px 64px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.05)',
                border: achievement.unlockedAt 
                  ? '1px solid rgba(156, 106, 222, 0.3)'
                  : '1px solid rgba(255,255,255,0.4)',
                backdropFilter: 'blur(20px)',
                transform: achievement.unlockedAt ? 'scale(1.02)' : 'scale(1)'
              }}>
                <CardContent className="p-6 text-center relative z-10">
                  <div className="space-y-4">
                    <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center text-2xl transition-all duration-500 ${achievement.unlockedAt ? 'animate-pulse' : ''}`} style={{ 
                      background: achievement.unlockedAt 
                        ? 'var(--gradient-celebration)'
                        : 'var(--color-background-secondary)',
                      boxShadow: achievement.unlockedAt 
                        ? '0 8px 24px rgba(156, 106, 222, 0.4)'
                        : '0 4px 12px rgba(0,0,0,0.1)'
                    }}>
                      <span className={achievement.unlockedAt ? 'text-white' : 'text-gray-500'}>
                        {achievement.icon}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2" style={{ 
                        color: achievement.unlockedAt ? 'var(--color-lavender)' : 'var(--color-text)',
                        textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}>
                        {achievement.name}
                      </h3>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                        {achievement.description}
                      </p>
                    </div>
                    <div className="flex items-center justify-center space-x-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRarityColor(achievement.rarity)}`} style={{
                        background: achievement.unlockedAt ? 'var(--gradient-spa)' : undefined,
                        color: achievement.unlockedAt ? 'white' : undefined
                      }}>
                        {achievement.rarity}
                      </span>
                      <span className="text-xs font-medium" style={{ color: 'var(--color-primary)' }}>
                        +{achievement.points} pts
                      </span>
                    </div>
                    {achievement.unlockedAt && (
                      <div className="text-xs font-semibold animate-bounce" style={{ color: 'var(--color-celebration)' }}>
                        🎉 Milestone Unlocked!
                      </div>
                    )}
                  </div>
                </CardContent>
                {achievement.unlockedAt && (
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-400/10 via-pink-300/5 to-teal-400/5 pointer-events-none animate-pulse"></div>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Challenges Tab */}
        <TabsContent value="challenges" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Active Challenges</h3>
            <Button size="sm" className="flex items-center space-x-1">
              <Plus className="w-4 h-4" />
              <span>Join Challenge</span>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {challenges.map((challenge) => (
              <Card key={challenge.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{challenge.name}</CardTitle>
                    <span className="px-2 py-1 rounded-full text-xs font-medium" style={{ 
                      background: challenge.status === 'active' ? 'var(--gradient-cta-primary)' : 'var(--gradient-cta-secondary)', 
                      color: 'var(--color-white)',
                      boxShadow: 'var(--shadow-lg)'
                    }}>
                      {challenge.status}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)', textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>{challenge.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <span>Participants</span>
                      <span>{challenge.participants.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <span>Ends</span>
                      <span>{challenge.endDate.toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                      <span>Rewards</span>
                      <span>{challenge.rewards.length} rewards</span>
                    </div>
                    <Button className="w-full" size="sm">
                      Join Challenge
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Community Leaderboard</h3>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-1" />
                Filter
              </Button>
              <Button variant="outline" size="sm">
                <Search className="w-4 h-4 mr-1" />
                Search
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {leaderboard.map((entry, index) => (
              <Card key={entry.userId} className="hover:shadow-md transition-shadow">
                <CardContent className="sculptd-card-padding">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full font-bold" style={{
                      background: 'var(--gradient-cta-primary)',
                      color: 'var(--color-white)',
                      boxShadow: 'var(--shadow-md)'
                    }}>
                      {entry.rank}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="font-semibold">{entry.username}</h4>
                        <div className="flex space-x-1">
                          {entry.badges.map((badge, badgeIndex) => (
                            <span key={badgeIndex} className="text-sm">{badge}</span>
                          ))}
                        </div>
                      </div>
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{entry.score} points</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-blue-600">{entry.score}</div>
                      <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>points</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Rewards Tab */}
        <TabsContent value="rewards" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <div className="p-3 bg-yellow-100 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                  <Gift className="w-8 h-8 text-yellow-600" />
                </div>
                <h4 className="font-semibold mb-2">Premium Feature</h4>
                <p className="text-sm mb-3" style={{ color: 'var(--color-text-muted)' }}>Unlock advanced analytics</p>
                <Button size="sm" className="w-full">
                  Claim Reward
                </Button>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <div className="p-3 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center" style={{
                  background: 'var(--gradient-cta-primary)',
                  boxShadow: 'var(--shadow-lg)'
                }}>
                  <Medal className="w-8 h-8" style={{ color: 'var(--color-white)' }} />
                </div>
                <h4 className="font-semibold mb-2">Discount Code</h4>
                <p className="text-sm text-gray-600 mb-3">20% off premium subscription</p>
                <Button size="sm" className="w-full">
                  Claim Reward
                </Button>
              </CardContent>
            </Card>
            <Card className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <div className="p-3 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center" style={{
                  background: 'var(--gradient-cta-secondary)',
                  boxShadow: 'var(--shadow-lg)'
                }}>
                  <Award className="w-8 h-8" style={{ color: 'var(--color-white)' }} />
                </div>
                <h4 className="font-semibold mb-2">Exclusive Badge</h4>
                <p className="text-sm text-gray-600 mb-3">Show off your achievement</p>
                <Button size="sm" className="w-full">
                  Claim Reward
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GamificationPanel;
